import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    rescheduleCourseModule,
    scheduleCourseModule,
    setOpenModuleSchedulerPanel
} from '../../../store/courseService';
import { loadLocationsForServices } from '../../../store/directusService';
import { formatDate, isTimePast, todayDate } from '../../../utils/dateFunctions';
import { getObjFromId } from '../../../utils/directusFunctions';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import SingleSelect from '../../formElements/SingleSelect';
import StaticField from '../../formElements/StaticField';
import TextInputField from '../../formElements/TextInputField';
import Panel from '../../ui/panel/Panel';
import StartAndEndTimesSelect from '../components/StartAndEndTimesSelect';
import { canEditModule, editPastModuleTimeRoles, getServiceIds } from '../utils/calendarUtils';
import { validate } from '../validation/validateModuleScheduler';

import sidePanel from '../styles/schedulePanel.module.css';

const ModuleScheduler = ({ currentModule, newEvent, onPending, adviser }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        title: '',
        locationId: '',
        capacity: 1,
        startTime: '08:00',
        endTime: '09:00',
        date: todayDate(),
        participants: []
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState({});
    const [errors, setErrors] = useState({});
    const [existingParticipantCount, setExistingParticipantCount] = useState(1);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [mode, setMode] = useState('create');
    const [isInPast, setIsInPast] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const locations = useSelector((state) => state.entities.directusService.locationsForServices);
    const openModuleScheduler = useSelector(
        (state) => state.entities.courseService.openModuleScheduler
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        const serviceIds = getServiceIds(adviser);
        serviceIds.length && dispatch(loadLocationsForServices(serviceIds));

        if (Object.keys(newEvent).length) {
            setNewEntry({
                ...newEvent,
                capacity: newEvent?.capacity || '1',
                userId: adviser?.id
            });
            mode !== 'create' && setMode('create');
            setIsInPast(isTimePast(`${newEvent?.date}T${newEvent.startTime}`));
        } else if (Object.keys(currentModule).length) {
            setNewEntry({
                ...currentModule,
                capacity: currentModule?.capacity || '1',
                userId: adviser.id
            });
            setInitialStateOnEntry(currentModule);

            if (currentModule?.participants?.length > 1)
                setExistingParticipantCount(currentModule.participants.length);
            mode !== 'edit' && setMode('edit');
            setIsInPast(isTimePast(`${currentModule.date}T${currentModule.startTime}`));
        }
    }, []);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (
            successMessage === `Module ${newEntry.title} has been scheduled` ||
            successMessage === `Module ${newEntry.title} has been rescheduled`
        ) {
            setIsButtonDisabled(false);
            onPanelClose();
        }
    }, [successMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    // EVENT HANDLERS
    const onPanelClose = () => {
        setNewEntry(initialState);
        !currentModule?.date ? onPending(newEvent) : onPending({});
        dispatch(setOpenModuleSchedulerPanel(false));
    };

    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateTime = (key, value) => {
        clearError(key === 'endTime' ? 'startTime' : 'endTime');
        onUpdate(key, value);
    };

    const onUpdateCapacity = (e) => {
        const value = e.target.value.slice(0, 2);
        onUpdate('capacity', value < 1 ? 1 : value);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(
            newEntry,
            initialStateOnEntry,
            roles,
            editPastModuleTimeRoles,
            mode
        );
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        if (Object.keys(newEvent) && newEvent?.moduleTemplateId)
            dispatch(scheduleCourseModule(newEntry));
        else if (Object.keys(currentModule)?.length) dispatch(rescheduleCourseModule(newEntry));
    };

    // RENDER
    return (
        <Panel width="500px" open={openModuleScheduler} onToggle={onPanelClose}>
            <div className={sidePanel.formWrapper}>
                <div className={sidePanel.moduleTitle}>{newEntry.title}</div>
                <form onSubmit={onSubmit}>
                    <StaticField
                        id="adviser"
                        label="Adviser"
                        content={adviser?.loginEmailAddress}
                    />

                    {canEditModule('location', roles, isInPast) ? (
                        <SingleSelect
                            id="locationId"
                            label="Location"
                            placeholder="Select location"
                            mandatory={true}
                            menuItems={locations || []}
                            selectedId={newEntry.locationId || currentModule.locationId || ''}
                            selected={getObjFromId(locations, newEntry.locationId)}
                            error={errors.locationId}
                            onChange={(id) => onUpdate('locationId', id)}
                        />
                    ) : (
                        <StaticField id="location" label="Location" content={newEntry.location} />
                    )}
                    {canEditModule('capacity', roles, isInPast) ? (
                        <TextInputField
                            id="numberOfSlots"
                            type="number"
                            label="Number of Slots"
                            mandatory={true}
                            min={existingParticipantCount || 1}
                            max={99}
                            value={newEntry.capacity || 1}
                            customClass="capacityInput"
                            onChange={onUpdateCapacity}
                        />
                    ) : (
                        <StaticField
                            id="numberOfSlots"
                            label="Number of Slots"
                            content={newEntry.capacity.toString()}
                        />
                    )}
                    {canEditModule('startAndEndTime', roles, isInPast) ? (
                        <StartAndEndTimesSelect
                            startTime={newEntry.startTime}
                            endTime={newEntry.endTime}
                            errors={errors}
                            onTimeChange={onUpdateTime}
                        />
                    ) : (
                        <>
                            <StaticField
                                id="startTime"
                                label="Start Time"
                                content={newEntry.startTime}
                            />
                            <StaticField id="endTime" label="End Time" content={newEntry.endTime} />
                        </>
                    )}
                    {canEditModule('date', roles, isInPast) ? (
                        <DateSelect
                            mandatory={true}
                            value={newEntry.date}
                            error={errors.date}
                            onDateChange={(date) => onUpdate('date', date)}
                        />
                    ) : (
                        <StaticField id="date" label="Date" content={formatDate(newEntry.date)} />
                    )}
                    <div className={sidePanel.updateButton}>
                        <Button
                            id="courseModuleSchedulerUpdateButton"
                            content="Update"
                            disabled={!canEditModule('button', roles, isInPast) || isButtonDisabled}
                            error={errors.button}
                            clearError={() => clearError('button')}
                            customErrorClass="rightSideButtonError"
                        />
                    </div>
                </form>
            </div>
        </Panel>
    );
};

ModuleScheduler.propTypes = {
    currentModule: PropTypes.object,
    newEvent: PropTypes.object,
    onPending: PropTypes.func,
    adviser: PropTypes.object
};

export default ModuleScheduler;
